import React, {useState, useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Box, styled} from '@mui/material';
import {Modal as ModalDesignSystem} from '@lightricks/react-design-system';
import {useQueryClient} from '@tanstack/react-query';
import translate from '@/utils/translate';
import {SocialPlatform} from '@/types/platforms';
import useSocialPlatforms from '@/hooks/use-social-platforms';
import SocialNetworksList from './SocialNetworksList';
import LegalSocialNote from './LegalSocialNote';
import SupportSection from './SupportSection';
import {unlinkSocialNetwork} from '@/api/updaters/socialNetworks';
import {
  ERROR_MESSAGE_PARAM_KEY,
  ERROR_PROVIDER_PARAM_KEY,
} from '@/config/constants';
import useGenericButtonPressedEvent from '@/hooks/analytics/use-generic-button-pressed-event';
import {screenNames} from '@/lib/delta/deltaConstants';

const StyledContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
}));

/**
 * ConnectSocialAccountPanel
 *
 * This component contains all the business logic and UI for social account management:
 * - Social networks list with connection buttons and connected account displays
 * - Legal note about privacy and terms
 * - Support section with FAQ links
 * - Error handling for connection failures
 * - Unlink confirmation modal
 *
 * Can be used standalone in settings pages or embedded in modals.
 */
function ConnectSocialAccountPanel() {
  const [loadingPlatform, setLoadingPlatform] = useState<SocialPlatform | null>(
    null
  );
  const [platformToUnlink, setPlatformToUnlink] = useState<string | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const {pressed} = useGenericButtonPressedEvent();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryClient = useQueryClient();

  const showUnlinkConfirmationModal = !!platformToUnlink;

  const {user, availablePlatforms, connectedPlatforms, authenticatePlatform} =
    useSocialPlatforms();

  // Read error parameters from URL
  const errorMessage = searchParams.get(ERROR_MESSAGE_PARAM_KEY);
  const errorProvider = searchParams.get(ERROR_PROVIDER_PARAM_KEY);

  const handleConnect = useCallback(
    async (platform: SocialPlatform) => {
      try {
        pressed({
          button_name: `Connect ${platform}`,
          screen_name: screenNames.connect_social_accounts.pop_up,
        });
        setLoadingPlatform(platform);
        await authenticatePlatform(platform);
      } catch (error) {
        console.error(`Failed to connect ${platform}:`, error);
      } finally {
        setLoadingPlatform(null);
      }
    },
    [authenticatePlatform]
  );

  const handleUnlink = useCallback((provider: string) => {
    pressed({
      button_name: `Unlink ${provider}`,
      screen_name: screenNames.connect_social_accounts.pop_up,
    });
    setPlatformToUnlink(provider);
  }, []);

  const confirmUnlink = useCallback(async () => {
    if (!platformToUnlink) return;

    try {
      pressed({
        button_name: `Confirm unlink ${platformToUnlink}`,
        screen_name: screenNames.connect_social_accounts.pop_up,
      });
      setIsUnlinking(true);

      await unlinkSocialNetwork(platformToUnlink);

      // Invalidate user query to refresh connected platforms
      await queryClient.invalidateQueries({queryKey: ['user']});

      console.log('Account unlinked successfully');
    } catch (error: unknown) {
      const unlinkErrorMessage =
        (error as any)?.payload?.errors?.[0] ||
        'Failed to unlink account. Please try again.';
      console.error('Unlink failed:', unlinkErrorMessage);
    } finally {
      setIsUnlinking(false);
      setPlatformToUnlink(null);
    }
  }, [platformToUnlink, queryClient]);

  const cancelUnlink = useCallback(() => {
    setPlatformToUnlink(null);
  }, []);

  const handleRelink = useCallback(
    async (provider: string) => {
      try {
        pressed({
          button_name: `Relink ${provider}`,
          screen_name: screenNames.connect_social_accounts.pop_up,
        });
        setIsUnlinking(true);

        // First unlink the existing account
        await unlinkSocialNetwork(provider);

        // Find the matching platform from available platforms
        // Provider names like 'instagram' should match platform names
        const matchingPlatform = availablePlatforms.find(
          (platform) => platform.toLowerCase() === provider.toLowerCase()
        );

        if (matchingPlatform) {
          // Trigger authentication flow for relinking
          await authenticatePlatform(matchingPlatform);

          console.log('Account relinked successfully');
        } else {
          throw new Error(`Platform ${provider} not found`);
        }
      } catch (error: unknown) {
        const relinkErrorMessage =
          (error as any)?.payload?.errors?.[0] ||
          'Failed to relink account. Please try again.';
        console.error('Relink failed:', relinkErrorMessage);
      } finally {
        setIsUnlinking(false);
      }
    },
    [authenticatePlatform, availablePlatforms]
  );

  const clearErrorParams = useCallback(() => {
    // Clear error parameters from URL
    if (errorMessage || errorProvider) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(ERROR_MESSAGE_PARAM_KEY);
      newSearchParams.delete(ERROR_PROVIDER_PARAM_KEY);
      setSearchParams(newSearchParams);
    }
  }, [errorMessage, errorProvider, searchParams, setSearchParams]);

  const handleDismissError = useCallback(() => {
    clearErrorParams();
  }, [clearErrorParams]);

  return (
    <StyledContainer>
      <SocialNetworksList
        platforms={availablePlatforms}
        connectedPlatforms={connectedPlatforms}
        user={user}
        onConnect={handleConnect}
        onUnlink={handleUnlink}
        onRelink={handleRelink}
        isLoading={loadingPlatform !== null || isUnlinking}
        loadingPlatform={loadingPlatform}
        errorProvider={errorProvider || undefined}
        errorMessage={errorMessage || undefined}
        onDismissError={errorMessage ? handleDismissError : undefined}
      />
      <LegalSocialNote />
      <SupportSection />

      {/* Unlink Confirmation Modal */}
      <ModalDesignSystem
        open={showUnlinkConfirmationModal}
        handleClose={cancelUnlink}
        header={translate('Unlink Social Account')}
        textAlignment="center"
        footerAlignment="center"
        primaryButtonText={translate('Unlink')}
        primaryButtonProps={{
          appearance: 'brand',
          disabled: isUnlinking,
        }}
        handlePrimaryButtonClick={confirmUnlink}
        secondaryButtonText={translate('Cancel')}
        secondaryButtonProps={{
          disabled: isUnlinking,
        }}
        handleSecondaryButtonClick={cancelUnlink}
      >
        {translate(
          'Are you sure you want to unlink? Your {platform} account will be unlinked.',
          {platform: platformToUnlink}
        )}
      </ModalDesignSystem>
    </StyledContainer>
  );
}

export default ConnectSocialAccountPanel;
